import React, { useReducer, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { Form } from 'informed';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { withRouter } from 'react-router-dom';
import { Spinner, InputTypeArea } from '../../elements';
import DocumentsDetailReducer from '../../reducers/DocumentsDetailReducer';
import { fetchPersonalDataSummary } from '../../services/personal_data_service';
import {
  fetchDocumentTypes,
  fetchDocuments,
  sendDocuments
} from '../../services/documents_services';
import { handleDocumentUpload } from '../../services/upload_service';
import { API_URL } from '../../../lib/const';
import Modal from '../../components/ConfirmationModal';
import DocumentsDetailsView from '../components/DocumentsDetailsView';
import DocumentsDetailsForm from '../components/DocumentsDetailsForm';
import withPermission from '../../../hoc/withPermission';
import { ENROLLMENT_STATUS } from '../../../lib/config';
import BatchDownloadDocumentsModal from '../../v2/documents/components/BatchDownloadDocumentsModal';
import AppropriationAlert from '../components/AppropriationAlert';
import {
  fetchAppropriations,
  createAppropriation,
  transferAppropriation
} from '../../services/document_appropriation_service';
import { fetchCurrentUser } from '../../services/user_services';

const DocumentsDetailPage = ({ match, history, hasPermission, isAdmin }) => {
  const initialState = {
    loadingPage: false,
    isEditing: false,
    showPendencies: false,
    hasPendencies: false,
    loadingPendencies: false,
    pendencies: [],
    errors: {},
    personalDocumentsTypes: [],
    personalDocuments: [],
    personalData: {},
    form: {},
    pendingDocument: null,
    enrollmentId: 0,
    appropriations: [],
    activeAppropriation: null,
    loadingAppropriation: true,
    currentUser: null
  };
  const [state, dispatch] = useReducer(DocumentsDetailReducer, initialState);
  const {
    loadingPage,
    personalData,
    loadingPendencies,
    personalDocumentsTypes,
    personalDocuments,
    isEditing,
    confirmationModal,
    showPendencies,
    form,
    pendencies,
    errors,
    pendingDocument,
    showBatchDownloadDocumentsModal,
    enrollmentId,
    appropriations,
    activeAppropriation,
    loadingAppropriation,
    currentUser
  } = state;

  const { token } = match.params;

  const fetchPersonalData = () => {
    fetchPersonalDataSummary(token).then(personalDataSummary => {
      dispatch({
        type: 'SET_PERSONAL_DATA',
        payload: personalDataSummary
      });
    });
  };

  const fetchPersonalDocuments = () => {
    fetchDocuments(token).then(res =>
      dispatch({ type: 'SET_PERSONAL_DOCUMENTS', payload: res.documents })
    );
  };

  const fetchPendencies = id => {
    const dadosPessoais = 'Dados pessoais';
    dispatch({ type: 'SET_LOADING_PENDENCIES' });
    axios
      .get(`${API_URL}/onboarding/pendencies`, {
        params: {
          enrollment: id
        }
      })
      .then(response => {
        const activePendencies = response.data.filter(f => !f.resolved_at);
        dispatch({
          type: 'SET_PENDENCIES_LIST',
          payload: response.data.filter(p => p.step === dadosPessoais)
        });
        if (activePendencies.length > 0) {
          dispatch({ type: 'SET_PENDENCIES_LIST_VISIBILITY', payload: true });
        }
      })
      .catch(() => {
        dispatch({ type: 'SET_LOADING_PENDENCIES' });
      });
  };

  const fetchData = () => {
    dispatch({ type: 'SET_LOADING_PAGE', payload: true });
    axios
      .all([
        fetchPersonalDataSummary(token),
        fetchDocumentTypes(token),
        fetchDocuments(token)
      ])
      .then(([personalDataSummary, documentsTypes, documents]) => {
        fetchPendencies(personalDataSummary.enrollment.id);
        dispatch({
          type: 'SET_ENROLLMENT_ID',
          payload: personalDataSummary.enrollment.id
        });
        dispatch({
          type: 'SET_PERSONAL_DATA',
          payload: personalDataSummary
        });
        dispatch({
          type: 'SET_PERSONAL_DOCUMENTS_TYPES',
          payload: documentsTypes
        });
        dispatch({
          type: 'SET_PERSONAL_DOCUMENTS',
          payload: documents.documents
        });
      });
  };

  const fetchAppropriationData = (id = enrollmentId) => {
    if (!id) return;

    dispatch({ type: 'SET_LOADING_APPROPRIATION', payload: true });
    fetchAppropriations(id)
      .then(data => dispatch({ type: 'SET_APPROPRIATIONS', payload: data || [] }))
      .catch(() => {
        dispatch({ type: 'SET_LOADING_APPROPRIATION', payload: false });
        toast.error('Não foi possível carregar as apropriações da matrícula.');
      });
  };

  const handleAppropriate = async () => {
    dispatch({ type: 'SET_LOADING_APPROPRIATION', payload: true });
    try {
      await createAppropriation(enrollmentId);
      toast.success('Matrícula apropriada com sucesso!');
    } catch (e) {
      toast.error('Não foi possível apropriar a matrícula. Tente novamente.');
    }
    fetchAppropriationData();
  };

  const handleTransferAppropriation = async (appropriationId, userId) => {
    dispatch({ type: 'SET_LOADING_APPROPRIATION', payload: true });
    try {
      await transferAppropriation(appropriationId, userId);
      toast.success('Apropriação transferida com sucesso!');
    } catch (e) {
      const errors = get(e, 'response.data.non_field_errors')
        || get(e, 'response.data.user');
      toast.error(
        (errors && errors[0])
          || 'Não foi possível transferir a apropriação. Tente novamente.'
      );
    }
    fetchAppropriationData();
  };

  const sendPendencies = () => {
    dispatch({ type: 'SET_LOADING_PENDENCIES' });
    axios
      .post(`${API_URL}/onboarding/enrollments/${token}/pendency`)
      .then(() => {
        fetchData();
        // O envio das pendências encerra a apropriação.
        fetchAppropriationData();
        toast.success('Pendências enviadas com sucesso!');
        dispatch({ type: 'SET_LOADING_PENDENCIES' });
        dispatch({ type: 'SET_PENDENCIES_LIST_VISIBILITY', payload: false });
      })
      .catch(() => {
        toast.error(
          'Ocorreu um erro ao enviar a(s) pendência(s). Por favor tente novamente.'
        );
        dispatch({ type: 'SET_LOADING_PENDENCIES' });
      });
  };

  const handleUpload = async (event, document_type) => {
    const file = get(event, 'target.files.0');
    try {
      const response = await handleDocumentUpload(file, document_type);
      const { key } = response;
      const newPersonalDocument = { key, document_type, name: file.name };
      dispatch({
        type: 'ADD_PERSONAL_DOCUMENTS',
        payload: newPersonalDocument
      });
    } catch (error) {
      toast.error('Ocorreu um erro ao enviar arquivo. Tente novamente.');
    }
  };

  const handleRemoveDocument = key => {
    const newListDocuments = personalDocuments.filter(d => d.key !== key);
    dispatch({
      type: 'REMOVE_PERSONAL_DOCUMENTS',
      payload: newListDocuments
    });
  };

  const handlePendency = async () => {
    const dadosPessoais = 2;

    if (pendingDocument) {
      await axios
        .patch(`${API_URL}/documents/documents-data/${token}/review_document`, {
          document_id: pendingDocument.id,
          approved: false
        })
        .then(() => {
          toast.success('Documento reprovado!');
          fetchPersonalDocuments(token);
        })
        .catch(e => {
          console.error(e);
          return toast.error('Algo deu errado.');
        });
    }

    await axios
      .post(`${API_URL}/onboarding/pendencies`, {
        ...form,
        step: dadosPessoais,
        enrollment: personalData.enrollment.id
      })
      .then(() => {
        dispatch({ type: 'SET_PENDENCIES_LIST_VISIBILITY', payload: false });
        dispatch({ type: 'CLOSE_PENDENCIES_VISIBILITY' });
        fetchData();
      })
      .catch(() => {
        toast.error(
          'Ocorreu um erro ao criar pendência. Por favor tente novamente mais tarde'
        );
      });
  };

  const handleDocumentApproval = async doc => {
    if (!window.confirm('Tem certeza de que deseja aprovar o documento'))
      return;

    await axios
      .patch(`${API_URL}/documents/documents-data/${token}/review_document`, {
        document_id: doc.id,
        approved: true
      })
      .then(res => {
        toast.success('Documento aprovado!');
        fetchPersonalDocuments(token);
      })
      .catch(e => {
        console.error(e);
        return toast.error('Algo deu errado.');
      });
  };

  const handleDocumentReproval = async p_document => {
    dispatch({ type: 'OPEN_PENDENCIES_VISIBILITY', payload: p_document });
  };

  const getPersonalDocuments = document_type =>
    personalDocuments.filter(d => d.document_type === document_type);

  const handleSavePersonal = async () => {
    dispatch({ type: 'SET_LOADING_PAGE', payload: true });
    try {
      await sendDocuments(token, { documents: personalDocuments });
      window.scroll(0, 0);
      dispatch({ type: 'SET_EDITING' });
      fetchPersonalData();
      fetchPersonalDocuments();
      // A atualização dos documentos encerra a apropriação.
      fetchAppropriationData();
    } catch (err) {
      toast.error('Ocorreu um erro ao editar o formulário.');
      dispatch({
        type: 'SET_DOCUMENTS_ERROR',
        payload: get(err, 'response.data')
      });
    }
    dispatch({ type: 'SET_LOADING_PAGE', payload: false });
  };

  const handleApproveData = async () => {
    await axios.post(
      `${API_URL}/documents/documents-data/${token}/approve_data`
    );
    toast.success('Aluno aprovado com sucesso!');
    dispatch({ type: 'SET_CONFIRMATION_MODAL' });
    fetchData();
    // A aprovação encerra a apropriação.
    fetchAppropriationData();
  };

  const handleModal = () => {
    dispatch({ type: 'SET_CONFIRMATION_MODAL' });
  };

  const handleEdit = () => {
    dispatch({ type: 'SET_EDITING' });
  };

  const canApprove = () => {
    const document_status = get(personalData, 'enrollment.document_status', {});
    const status = get(personalData, 'enrollment.status', {});
    return (
      get(document_status, 'id') === 3 &&
      [
        ENROLLMENT_STATUS.PAYMENT_REVIEWING,
        ENROLLMENT_STATUS.REVIEWING,
        ENROLLMENT_STATUS.WAITING_PAYMENT
      ].includes(status.id)
    );
  };

  const handleAction = action => {
    if (action === 'DOWNLOAD_PERSONAL_DOCUMENTS') {
      dispatch({ type: 'OPEN_BATCH_DOWNLOAD_DOCUMENTS_MODAL' });
    }
  };

  const unresolvedPendencies = pendencies.filter(f => !f.resolved_at);
  // Pendências criadas na análise que ainda não foram enviadas ao candidato.
  const unsentPendencies = unresolvedPendencies.filter(f => !f.sent_at);
  // Matrícula em análise ou aguardando o candidato resolver as pendências: nos
  // dois casos o atendente pode revisar os documentos.
  const canReview = () => canApprove()
    || get(personalData, 'enrollment.status.id') === ENROLLMENT_STATUS.PENDING;

  // A matrícula só pode ser aprovada depois que todos os documentos enviados
  // forem aprovados individualmente.
  const allDocumentsApproved = personalDocuments.length > 0
    && personalDocuments.every(d => d.approved === true);

  // A análise documental (auditar documentos, criar/editar/enviar pendências e
  // aprovar) só fica disponível para quem está apropriado da matrícula.
  const isAppropriatedByMe = !!(
    activeAppropriation
    && currentUser
    && activeAppropriation.user.id === currentUser.id
  );

  // A transferência é do usuário apropriado, mas o superusuário também pode
  // executá-la (mesma regra validada na API).
  const canTransferAppropriation = !!(
    activeAppropriation
    && (isAppropriatedByMe || (currentUser && currentUser.is_superuser))
  );

  // Mesmo critério da Graduação: documentação em análise (matrícula em análise,
  // aguardando pagamento ou em análise do pagamento) ou matrícula com pendências.
  const showAppropriationAlert = !loadingAppropriation && canReview();

  useEffect(() => {
    fetchData();
    fetchCurrentUser()
      .then(user => dispatch({ type: 'SET_CURRENT_USER', payload: user }))
      .catch(() => toast.error('Não foi possível identificar o usuário logado.'));
  }, []);

  useEffect(() => {
    fetchAppropriationData(enrollmentId);
  }, [enrollmentId]);
  if (hasPermission('personal.only_see_finished_enroll') && !isAdmin()) {
    return (
      <div className="container">
        <Spinner active={loadingPage}>
          <div className="one-page-only mb-4">
            <DocumentsDetailsView
              personalData={personalData}
              personalDocumentsTypes={personalDocumentsTypes}
              personalDocuments={personalDocuments}
              pendencies={pendencies}
              loadingPendencies={loadingPendencies}
              reloadPendencies
              showPendencies={false}
              showActions
              onHandleAction={handleAction}
            />
            <div className="btn-actions offset-md-8 mb-4 col-md-4 mt-4 p-0 col-sm-12">
              <button
                type="button"
                className="btn btn-black mr-2"
                onClick={() => history.push('/academic')}
              >
                Voltar
              </button>
            </div>
          </div>
          <BatchDownloadDocumentsModal
            visible={showBatchDownloadDocumentsModal}
            onClose={() =>
              dispatch({ type: 'CLOSE_BATCH_DOWNLOAD_DOCUMENTS_MODAL' })
            }
            documents={personalDocuments}
          />
        </Spinner>
      </div>
    );
  }

  return (
    <div className="container">
      <Spinner active={loadingPage}>
        <div className="one-page-only mb-4">
          {showAppropriationAlert && (
            <AppropriationAlert
              activeAppropriation={activeAppropriation}
              appropriations={appropriations}
              currentUser={currentUser}
              onAppropriate={handleAppropriate}
              loading={loadingAppropriation}
            />
          )}
          {!isEditing &&
            isAppropriatedByMe && (
              <>
                {canApprove() && (
                <button
                  type="button"
                  className="float-button mr-6"
                  onClick={() => handleEdit()}
                >
                  <FontAwesomeIcon
                    spin={loadingPage}
                    icon={loadingPage ? 'spinner' : 'edit'}
                  />
                </button>
                )}
                {canReview() && (
                <button
                  type="button"
                  className="float-button"
                  onClick={() =>
                    dispatch({ type: 'OPEN_PENDENCIES_VISIBILITY' })
                  }
                >
                  <FontAwesomeIcon
                    spin={loadingPage}
                    icon={loadingPage ? 'spinner' : 'plus'}
                  />
                </button>
                )}
              </>
            )}
          {isEditing ? (
            <DocumentsDetailsForm
              onSavePersonal={handleSavePersonal}
              personalDocumentsTypes={personalDocumentsTypes}
              onUpload={handleUpload}
              onRemoveDocument={handleRemoveDocument}
              getPersonalDocuments={getPersonalDocuments}
              onEdit={handleEdit}
              errors={errors}
            />
          ) : (
            <DocumentsDetailsView
              onApproveDocument={handleDocumentApproval}
              onReproveDocument={handleDocumentReproval}
              personalData={personalData}
              personalDocumentsTypes={personalDocumentsTypes}
              personalDocuments={personalDocuments}
              reloadPendencies={fetchPendencies}
              pendencies={pendencies}
              loadingPendencies={loadingPendencies}
              showActions
              enrollmentId={get(personalData.enrollment, 'id')}
              onHandleAction={handleAction}
              canAuditDocuments={isAppropriatedByMe}
              canEditPendencies={isAppropriatedByMe}
              showAppropriationHistory
              appropriations={appropriations}
              canTransferAppropriation={canTransferAppropriation}
              onTransferAppropriation={handleTransferAppropriation}
              loadingAppropriation={loadingAppropriation}
            />
          )}
          <div className="btn-actions offset-md-8 mb-4 col-md-4 mt-4 p-0 col-sm-12">
            <button
              type="button"
              className="btn btn-black mr-2"
              onClick={() => history.push('/academic')}
            >
              Voltar
            </button>
            {/* Com pendências novas, ainda não enviadas, a ação é enviá-las. */}
            {unsentPendencies.length > 0 &&
              isAppropriatedByMe && (
              <button
                className="btn btn-primary"
                type="submit"
                disabled={loadingPendencies}
                onClick={sendPendencies}
              >
                Enviar todas pendências
              </button>
            )}
            {/* Sem pendências a enviar, a ação é aprovar, liberada quando todos os
                documentos estiverem aprovados. */}
            {canReview() &&
              unsentPendencies.length === 0 &&
              isAppropriatedByMe && (
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={isEditing || !allDocumentsApproved}
                  title={
                    allDocumentsApproved
                      ? undefined
                      : 'Aprove todos os documentos antes de aprovar a matrícula'
                  }
                  onClick={() => handleModal()}
                >
                  {isEditing ? 'Editando' : 'Aprovar'}
                </button>
              )}
          </div>
        </div>
        <Modal
          visible={confirmationModal}
          title="Confirmação"
          onCancel={() => dispatch({ type: 'SET_CONFIRMATION_MODAL' })}
          onConfirm={handleApproveData}
        >
          Você tem certeza que deseja aprovar este aluno?
        </Modal>
        <BatchDownloadDocumentsModal
          visible={showBatchDownloadDocumentsModal}
          onClose={() =>
            dispatch({ type: 'CLOSE_BATCH_DOWNLOAD_DOCUMENTS_MODAL' })
          }
          documents={personalDocuments}
        />
        <CSSTransition
          in={showPendencies}
          timeout={300}
          classNames="expand-animation"
          unmountOnExit
        >
          <aside className="financer-float">
            <button
              className="btn-none float-right"
              type="button"
              onClick={() => dispatch({ type: 'CLOSE_PENDENCIES_VISIBILITY' })}
            >
              <FontAwesomeIcon icon="times" />
            </button>
            <h4 className="mt-3 bt-3">Pendências</h4>
            <hr />
            <Form
              id="pendencias"
              onValueChange={values =>
                dispatch({ type: 'SET_FORM', payload: values })
              }
            >
              <InputTypeArea
                field="description"
                initialValue={
                  pendingDocument
                    ? `Pendência para documento: ${
                        pendingDocument.document_type
                      } \n`
                    : ''
                }
                label="Descrição"
                row="10"
              />
              <button
                type="submit"
                className="btn btn-primary"
                onClick={() => handlePendency()}
              >
                Criar Pendência
              </button>
            </Form>
          </aside>
        </CSSTransition>
      </Spinner>
    </div>
  );
};

DocumentsDetailPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      token: PropTypes.string
    })
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.string
  }).isRequired,
  hasPermission: PropTypes.func.isRequired,
  isAdmin: PropTypes.func.isRequired
};

export default withRouter(withPermission(DocumentsDetailPage));
